import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MUIDataTable from 'mui-datatables';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Box,
  Grow,
  LinearProgress,
  makeStyles,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { uniqBy } from 'lodash-es';

import dataTableService from '../../services/dataTableService';
import { history, dataTableHelper } from '../../helpers';
import CustomSearchRender from './CustomSearchRender';

const useStyles = makeStyles((theme) => ({
  selectIcon: {
    marginRight: '8px',
    top: 'auto',
  },
  tabsContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '47px',
    paddingLeft: '24px',
  },
  tabSelected: {
    color: theme.palette.primary.main,
  },
  tabsIndicator: {
    backgroundColor: theme.palette.primary.main,
  },
}));

const invalidData = [null, undefined, NaN, ''];

function DataTable({
  columns,
  customRenderForFixedFilterColumnIndex,
  fetchUrl,
  hideColumnsOnActive,
  hideColumnsOnDeleted,
  itemUrl,
  keys,
  property,
  propertyToInject,
  showFixedFilterForColumnIndex,
  title,
  withTabs,
}) {
  const classes = useStyles();
  const intl = useIntl();
  const [data, setData] = useState();
  const [rawData, setRawData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [tab, setTab] = useState(0);
  const [columnsToDisplay, setColumnsToDisplay] = useState(columns);

  const allText = intl.formatMessage({ id: 'dataTable.all', defaultMessage: 'TODOS' });
  const filterByText = intl.formatMessage({ id: 'dataTable.filterBy', defaultMessage: 'Filtrar por' });
  const filename = `${title}.csv`;

  const hiddenColumns = useMemo(() => {
    if (!withTabs) return [];
    if (tab === 0) return hideColumnsOnActive || [];
    if (tab === 1) return hideColumnsOnDeleted || [];
    return [];
  }, [withTabs, tab, hideColumnsOnActive, hideColumnsOnDeleted]);

  const displayedColumns = useMemo(() => {
    if (!hiddenColumns.length) return columnsToDisplay;
    return columnsToDisplay.filter((col) => !hiddenColumns.includes(col.name));
  }, [columnsToDisplay, hiddenColumns]);

  const filterColumnInfo = useMemo(() => {
    if (showFixedFilterForColumnIndex === false || showFixedFilterForColumnIndex === undefined) {
      return { dataIndex: -1, displayIndex: -1, label: null };
    }

    const originalCol = columns[showFixedFilterForColumnIndex];
    if (!originalCol) return { dataIndex: -1, displayIndex: -1, label: null };

    const columnName = originalCol.name;

    return {
      dataIndex: keys.indexOf(columnName),
      displayIndex: displayedColumns.findIndex((c) => c.name === columnName),
      label: originalCol.label || columnName,
    };
  }, [columns, displayedColumns, keys, showFixedFilterForColumnIndex]);

  const transformResponseData = useCallback((records = [], extraData) => records.map((record) => {
    const row = keys.map((key) => invalidData.includes(record[key]) ? '-' : record[key]);

    if (extraData && typeof extraData === 'string') row.push(extraData);

    return row;
  }), [keys]);

  const onFilter = ({ target: { value } }) => {
    setSelectedFilter(value);

    const newCols = columnsToDisplay.map((c) => ({ ...c, options: { ...(c.options || {}) } }));
    const filterList = value !== 'all' ? [value] : [];
    const idx = showFixedFilterForColumnIndex;

    if (idx !== false && idx !== undefined && newCols[idx]) {
      newCols[idx].options.filterList = filterList;
    }

    setColumnsToDisplay(newCols);
  };

  const handleTabChange = useCallback((_, v) => {
    setTab(v);
    setSelectedFilter('all');

    setColumnsToDisplay(columns.map((c) => {
      const opts = { ...(c.options || {}) };
      if (opts.filterList) opts.filterList = [];
      return { ...c, options: opts };
    }));
  }, [columns]);

  const options = {
    textLabels: {
      body: {
        noMatch: intl.formatMessage({ id: 'dataTable.noMatch', defaultMessage: 'No hay datos para mostrar' }),
        toolTip: intl.formatMessage({ id: 'dataTable.toolTip', defaultMessage: 'Ordenar' }),
        columnHeaderTooltip: (column) => `${intl.formatMessage({ id: 'dataTable.sortTooltip', defaultMessage: 'Ordenamiento para' })} ${column.label}`,
      },
      pagination: {
        next: intl.formatMessage({ id: 'dataTable.next', defaultMessage: 'Página siguiente' }),
        previous: intl.formatMessage({ id: 'dataTable.previous', defaultMessage: 'Página anterior' }),
        rowsPerPage: intl.formatMessage({ id: 'dataTable.rowsPerPage', defaultMessage: 'Resultados:' }),
        displayRows: intl.formatMessage({ id: 'dataTable.displayRows', defaultMessage: 'de' }),
      },
      toolbar: {
        search: intl.formatMessage({ id: 'dataTable.search', defaultMessage: 'Buscar' }),
        downloadCsv: intl.formatMessage({ id: 'dataTable.downloadCSV', defaultMessage: 'Descargar CSV' }),
        filterTable: intl.formatMessage({ id: 'dataTable.filterTable', defaultMessage: 'Filtrar tabla' }),
      },
      filter: {
        all: intl.formatMessage({ id: 'dataTable.all', defaultMessage: 'TODOS' }),
        title: (<b>{intl.formatMessage({ id: 'dataTable.filters', defaultMessage: 'Filtros' })}</b>),
        reset: intl.formatMessage({ id: 'dataTable.reset', defaultMessage: 'REINICIAR' }),
      },
      viewColumns: {
        title: intl.formatMessage({ id: 'dataTable.showColumns', defaultMessage: 'Mostrar columnas' }),
        titleAria: intl.formatMessage({ id: 'dataTable.showTableColumns', defaultMessage: 'Mostrar/Ocultar Columnas de la tabla' }),
      },
    },
    downloadOptions: {
      filename,
    },
    selectableRows: 'none',
    print: false,
    viewColumns: false,
    searchOpen: true,
    onCellClick: (colData, cellMeta) => {
      if (!colData?.$$typeof) history.push(itemUrl + data[cellMeta.dataIndex][0]);
    },
    customSearchRender: (searchText, handleSearch, hideSearch, opt) => (
      <Box display="flex">
        <CustomSearchRender
          searchText={searchText}
          onSearch={handleSearch}
          onHide={hideSearch}
          options={opt}
        />
        {filterColumnInfo.displayIndex !== -1 && (
          <Grow appear in timeout={300}>
            <Box pl={4} display="flex" alignItems="center">
              <Typography variant="caption" style={{ fontWeight: 700, fontSize: 11, paddingRight: 16 }}>
                {filterByText} {filterColumnInfo.label}
              </Typography>

              <Select
                disableUnderline
                value={selectedFilter}
                onChange={onFilter}
                classes={{ icon: classes.selectIcon }}
              >
                <MenuItem value="all">{allText}</MenuItem>

                {uniqBy(data || [], (row) => row[filterColumnInfo.dataIndex]).map((row) => {
                  const value = row[filterColumnInfo.dataIndex];
                  return (
                    <MenuItem key={value} value={value}>
                      {customRenderForFixedFilterColumnIndex
                        ? customRenderForFixedFilterColumnIndex(value)
                        : value}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grow>
        )}
      </Box>
    ),

    onFilterChipClose: (index) => {
      if (index === showFixedFilterForColumnIndex) {
        onFilter({ target: { value: 'all' } });
      }
    },

    onDownload: (buildHead, buildBody, colName, rows) =>
      dataTableHelper.handleOnDownload(buildHead, buildBody, colName, rows, filename),
  };

  useEffect(() => {
    setColumnsToDisplay(columns);
  }, [columns]);

  useEffect(() => {
    setIsLoading(true);

    const fetchData = async () => {
      try {
        const response = await dataTableService.get(fetchUrl, { includeDelete: true });
        const records = property
          ? Object.values(response[property])
          : Array.isArray(response)
            ? response
            : Object.values(response);

        setRawData(records || []);
      } catch {
        setRawData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fetchUrl, property]);

  useEffect(() => {
    const filtered = withTabs
      ? rawData.filter((rec) =>
        tab === 0 ? rec.deleteDate === null : rec.deleteDate !== null
      )
      : rawData;

    const extraData = propertyToInject
      ? rawData?.[0]?.[propertyToInject]
      : '';

    setData(transformResponseData(filtered, extraData));
  }, [
    propertyToInject,
    rawData,
    tab,
    withTabs,
    transformResponseData,
  ]);

  if (isLoading) return <LinearProgress />;

  return (
    <>
      {withTabs && (
        <Box className={classes.tabsContainer}>
          <Tabs value={tab} onChange={handleTabChange} TabIndicatorProps={{ className: classes.tabsIndicator }}>
            <Tab classes={{ selected: classes.tabSelected }} label={intl.formatMessage({ id: 'dataTable.active', defaultMessage: 'Vigentes' })} />
            <Tab classes={{ selected: classes.tabSelected }} label={intl.formatMessage({ id: 'dataTable.deleted', defaultMessage: 'Eliminados' })} />
          </Tabs>
        </Box>
      )}

      <MUIDataTable
        columns={displayedColumns}
        data={selectedFilter === 'all' ? data : data.filter((row) => row[filterColumnInfo.dataIndex] === selectedFilter)}
        key={tab}
        options={options}
      />
    </>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array,
  customRenderForFixedFilterColumnIndex: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  fetchUrl: PropTypes.string,
  hideColumnsOnActive: PropTypes.array,
  hideColumnsOnDeleted: PropTypes.array,
  itemUrl: PropTypes.string,
  keys: PropTypes.array,
  property: PropTypes.string,
  propertyToInject: PropTypes.string,
  showFixedFilterForColumnIndex: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  title: PropTypes.string,
  withTabs: PropTypes.bool,
};

DataTable.defaultProps = {
  columns: [],
  customRenderForFixedFilterColumnIndex: false,
  fetchUrl: '',
  hideColumnsOnActive: [],
  hideColumnsOnDeleted: [],
  itemUrl: '',
  keys: [],
  property: null,
  propertyToInject: null,
  showFixedFilterForColumnIndex: false,
  title: '',
  withTabs: false,
};

export default DataTable;
